/*
 * Quarter ends are the last day of March, June, September, and December.
 * All dates are handled as plain YYYY-MM-DD strings to avoid timezone issues;
 * no Date objects are constructed unless we need to compute an actual calendar
 * boundary.
 */

import { parseCalendarDate } from '@backend/utils/calendarDate';
import { reportingDateOf } from '@backend/utils/reportingCalendar';

/** Month index (1-based) → last day of that month (non-leap-year safe for quarter ends). */
const QUARTER_END_DAYS: Record<number, number> = {
  '3': 31,
  '6': 30,
  '9': 30,
  '12': 31,
};

/** Last month of each quarter, indexed by the month's quarter position (1-based). */
const QUARTER_END_MONTHS = [3, 6, 9, 12];

/**
 * Format a year, month, and day as a YYYY-MM-DD string.
 */
function toDateStr(year: number, month: number, day: number): string {
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

/**
 * Return the YYYY-MM-DD date of the last day of the quarter that contains dateStr.
 *
 * Quarter ends: 03-31, 06-30, 09-30, 12-31.
 *
 * @param dateStr A date in YYYY-MM-DD format.
 * @returns The quarter-end date in YYYY-MM-DD format.
 */
export function quarterEndFor(dateStr: string): string {
  const [yearStr, monthStr] = dateStr.split('-');
  const year = parseInt(yearStr!, 10);
  const month = parseInt(monthStr!, 10);

  // Find the first quarter-end month >= the input month.
  const endMonth = QUARTER_END_MONTHS.find(m => m >= month)!;
  const endDay = QUARTER_END_DAYS[endMonth]!;

  return toDateStr(year, endMonth, endDay);
}

/**
 * Whether a calendar date is the last day of a calendar quarter.
 *
 * @param date - A date in YYYY-MM-DD
 * @returns true when the date is 31 Mar, 30 Jun, 30 Sep or 31 Dec
 */
export function isQuarterEnd(date: string): boolean {
  const parts = parseCalendarDate(date);
  if (!parts)
    return false;

  return QUARTER_END_MONTHS.includes(parts.month) && QUARTER_END_DAYS[parts.month] === parts.day;
}

/**
 * The first quarter end strictly after a date.
 *
 * @param date - A date in YYYY-MM-DD
 * @returns The next quarter end in YYYY-MM-DD
 */
export function nextQuarterEndAfter(date: string): string {
  const parts = parseCalendarDate(date);
  if (!parts)
    return date;

  for (const month of QUARTER_END_MONTHS) {
    const candidate = toDateStr(parts.year, month, QUARTER_END_DAYS[month]!);

    if (candidate > date)
      return candidate;
  }

  // Past 31 Dec, so the next one is next year's first.
  return toDateStr(parts.year + 1, 3, 31);
}

/**
 * The quarter-end that is complete as of a date: `date` itself when it is a quarter end
 * (it has fully elapsed), otherwise the quarter end before the quarter containing `date`.
 *
 * Used for a *historical* as-of window, where D has fully elapsed. Do not use this for "today" —
 * `lastCompletedQuarterEnd()` is the live-mode form, which always steps back because today has not
 * ended (see its own doc comment for why the two are not interchangeable).
 *
 * @param d - A date in YYYY-MM-DD format.
 * @returns The last quarter-end on or before `d`, in YYYY-MM-DD format.
 */
export function lastQuarterEndOnOrBefore(d: string): string {
  return isQuarterEnd(d) ? d : lastCompletedQuarterEnd(d);
}

/**
 * Today's date as a YYYY-MM-DD string, in the reporting zone (US Eastern — AC-31).
 *
 * Every read path that needs "today" calls this. Write paths (e.g. `services/forecast/recompute.ts`)
 * must keep calling this directly rather than reading `ctx.asOf.date` (`AsOfWindow`):
 * a write always happens live, and `today()` is the request-independent source of truth for it.
 *
 * @returns The current reporting-zone date in YYYY-MM-DD format.
 */
export function today(): string {
  return reportingDateOf(new Date());
}

/**
 * True when `value` is a well-formed calendar date strictly before today — the rule for a
 * usable historical as-of date. Shared by the frontend `?asOf=` query validation and the
 * backend `X-As-Of-Date` header validation so the two can never disagree.
 *
 * @param value - The raw date string to validate.
 * @returns Whether `value` parses as a calendar date and falls before today.
 */
export function isHistoricalDate(value: string): boolean {
  return parseCalendarDate(value) !== null && value < today();
}

/**
 * Return the quarter-end of the most recently *completed* quarter as of a date.
 *
 * The quarter containing `asOf` is still in progress, so this always steps back
 * to the one before it: 2026-05-22 yields 2026-03-31, and any January through
 * March date yields the prior year's 2026-12-31 form.
 *
 * This is the boundary every "overdue" and "stale" check in the product measures
 * against — quarterly NAV entry (`detectNavOverdue`) and book-value freshness
 * (SCR-21 §2 Line 3, `detectStaleBookValue`) both use it, so the two cannot drift.
 *
 * @param asOf The date to measure from, in YYYY-MM-DD format. Defaults to today.
 * @returns The last completed quarter-end in YYYY-MM-DD format.
 */
export function lastCompletedQuarterEnd(asOf: string = today()): string {
  return priorQuarterEnd(quarterEndFor(asOf));
}

/**
 * Return the quarter-end date one quarter before the given quarter-end.
 *
 * @param periodEnd A quarter-end date in YYYY-MM-DD format (e.g. "2024-06-30").
 * @returns The previous quarter-end date in YYYY-MM-DD format (e.g. "2024-03-31").
 */
export function priorQuarterEnd(periodEnd: string): string {
  const [yearStr, monthStr] = periodEnd.split('-');
  let year = parseInt(yearStr!, 10);
  let month = parseInt(monthStr!, 10) - 3;

  if (month <= 0) {
    month += 12;
    year -= 1;
  }

  return quarterEndFor(`${year}-${String(month).padStart(2, '0')}-01`);
}

/**
 * Derive the 1-based quarter number a quarter-end date falls in.
 *
 * @param periodEnd A quarter-end date in YYYY-MM-DD format (e.g., "2024-09-30").
 * @returns The quarter number, 1 through 4.
 */
export function quarterNumber(periodEnd: string): number {
  const [, monthStr] = periodEnd.split('-');
  const month = parseInt(monthStr!, 10);

  return QUARTER_END_MONTHS.indexOf(month) + 1;
}

/**
 * Format a quarter-end date as a human-readable quarter label.
 *
 * @param periodEnd A quarter-end date in YYYY-MM-DD format (e.g., "2024-09-30").
 * @returns A quarter label string (e.g., "Q3 2024").
 */
export function quarterLabel(periodEnd: string): string {
  const [yearStr] = periodEnd.split('-');
  const year = parseInt(yearStr!, 10);

  return `Q${quarterNumber(periodEnd)} ${year}`;
}

/**
 * Resolve a quarter and year to that quarter's last calendar day. The inverse of `quarterLabel`.
 *
 * @param quarter - The quarter, 1 through 4
 * @param year - The four-digit year
 * @returns The quarter-end date as YYYY-MM-DD
 */
export function quarterEndDate(quarter: number, year: number): string {
  return toDateStr(year, QUARTER_END_MONTHS[quarter - 1]!, QUARTER_END_DAYS[QUARTER_END_MONTHS[quarter - 1]!]!);
}

/**
 * Generate an array of quarter-end dates covering every quarter from
 * startDate through endDate (both inclusive).
 *
 * The first element is the quarter end for the quarter containing startDate;
 * the last is the quarter end for the quarter containing endDate. Returns an
 * empty array when endDate is strictly before startDate.
 *
 * @param startDate Start of the range in YYYY-MM-DD format.
 * @param endDate End of the range in YYYY-MM-DD format.
 * @returns Array of quarter-end dates in YYYY-MM-DD format, in ascending order.
 */
export function quarterRange(startDate: string, endDate: string): string[] {
  if (endDate < startDate)
    return [];

  const results: string[] = [];
  let current = quarterEndFor(startDate);

  while (current <= quarterEndFor(endDate)) {
    results.push(current);

    // Advance to the first day of the next quarter, then find its end.
    const [yearStr, monthStr] = current.split('-');
    let year = parseInt(yearStr!, 10);
    let month = parseInt(monthStr!, 10) + 1;

    if (month > 12) {
      month = 1;
      year += 1;
    }

    const next = quarterEndFor(toDateStr(year, month, 1));

    // Guard against infinite loops if quarterEndFor somehow returns the same value.
    if (next <= current)
      break;

    current = next;
  }

  return results;
}

/* What decides the period a fund's current KPIs are computed at. */
export interface KpiPeriodInput {
  // period end of the fund's latest NAV, or null when it has none
  latestNavPeriodEnd: string | null;
  // a fund flagged inactive with no NAVs is anchored to its last activity rather than today
  isActive: boolean;
  // latest LP capital event date
  lastLpDate: string | null;
  // latest portfolio lot date
  lastPortfolioDate: string | null;
  // the request's as-of date
  asOfDate: string;
}

/**
 * The period a fund's current KPIs are computed at (SCR-31 §5): the latest NAV's period end;
 * for an inactive fund with no NAVs, the quarter containing its last transaction; otherwise the
 * quarter containing the as-of date (in progress in live mode, so current-quarter transactions
 * count).
 *
 * @param input - The fund's NAV and activity dates
 * @returns The period end, YYYY-MM-DD
 */
export function currentKpiPeriod(input: KpiPeriodInput): string {
  if (input.latestNavPeriodEnd)
    return input.latestNavPeriodEnd;

  if (!input.isActive) {
    const lastTxnDate = [input.lastLpDate, input.lastPortfolioDate]
      .filter((d): d is string => d != null)
      .sort()
      .at(-1);

    return quarterEndFor(lastTxnDate ?? input.asOfDate);
  }

  return quarterEndFor(input.asOfDate);
}
